import {
  CloseOutlined,
  Add,
  Remove,
  TextureOutlined,
} from '@mui/icons-material';
import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { ShoppingListType } from '../../context/ShoppingListContext';

type Props = {
  setIsShoppingListOpen: (isOpen: boolean) => void;
  shoppingList: ShoppingListType[];
  removeProductFromShoppingList: (productId: number) => void;
  setProductQuantity: (productId: number, quantity: number) => void;
};

function ProductList({
  setIsShoppingListOpen,
  shoppingList,
  removeProductFromShoppingList,
  setProductQuantity,
}: Props) {
  return (
    <div className="space-y-4">
      <AnimatePresence mode="popLayout">
        {shoppingList.length > 0 ? (
          shoppingList.map((product) => (
            <motion.div
              key={product.id}
              className="relative bg-white rounded-lg border border-gray-100 overflow-hidden"
              initial={{ opacity: 0, y: -20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              transition={{ duration: 0.3 }}
              layout
              data-tooltip-id="my-tooltip"
              data-tooltip-content={product.name}
            >
              {/* Niestandardowy produkt - badge */}
              {!product.packaged && (
                <motion.div
                  className="absolute top-2 left-2 z-10 bg-amber-100 rounded-md px-2 py-1 flex items-center gap-1"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <TextureOutlined
                    fontSize="small"
                    className="text-amber-600"
                  />
                  <span className="text-xs font-medium text-amber-800">
                    Niestandardowy
                  </span>
                </motion.div>
              )}

              <div className="p-3 flex gap-3">
                {/* Obrazek produktu z linkiem */}
                <Link
                  to={`/produkt/${product.slug}`}
                  onClick={() => setIsShoppingListOpen(false)}
                  className="relative flex-shrink-0 w-20 h-20 overflow-hidden rounded-md bg-gray-50 border border-gray-100"
                >
                  <motion.img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-1 mix-blend-multiply"
                    initial={{ opacity: 0.8 }}
                    animate={{ opacity: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  />
                </Link>

                <div className="flex-grow flex flex-col">
                  {/* Nazwa produktu */}
                  <div className="mb-1">
                    <Link
                      className="group"
                      to={`/produkt/${product.slug}`}
                      onClick={() => setIsShoppingListOpen(false)}
                    >
                      <h3 className="text-sm font-medium text-gray-800 group-hover:text-primary transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      <div className="w-0 group-hover:w-full h-[1px] bg-primary transition-all duration-300"></div>
                    </Link>
                  </div>

                  {/* Cena i akcje */}
                  <div className="flex items-center justify-between mt-auto">
                    {/* Cena */}
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 text-sm">
                        {product.price.toFixed(2)} zł
                      </span>
                      <span className="text-xs text-gray-500">
                        Netto: {(product.price * 0.77).toFixed(2)} zł
                      </span>
                    </div>

                    {/* Kontrolki ilości */}
                    <div className="flex items-center gap-1">
                      {/* Zmniejsz ilość */}
                      <motion.button
                        className="w-7 h-7 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => {
                          const newQuantity = Math.max(1, product.quantity - 1);
                          setProductQuantity(product.id, newQuantity);
                        }}
                      >
                        <Remove fontSize="small" />
                      </motion.button>

                      {/* Ilość */}
                      <span className="w-6 text-center font-medium text-sm">
                        {product.quantity}
                      </span>

                      {/* Zwiększ ilość */}
                      <motion.button
                        className="w-7 h-7 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => {
                          const newQuantity = Math.min(
                            99,
                            product.quantity + 1,
                          );
                          setProductQuantity(product.id, newQuantity);
                        }}
                      >
                        <Add fontSize="small" />
                      </motion.button>
                    </div>
                  </div>
                </div>

                {/* Przycisk usuwania */}
                <motion.button
                  className="flex-shrink-0 self-start w-8 h-8 rounded-full flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors"
                  onClick={() => removeProductFromShoppingList(product.id)}
                  whileHover={{
                    scale: 1.1,
                    backgroundColor: 'rgb(254, 226, 226)',
                  }}
                  whileTap={{ scale: 0.9 }}
                >
                  <CloseOutlined fontSize="small" />
                </motion.button>
              </div>

              {/* Suma za ten produkt */}
              <div className="bg-gray-50 py-1 px-3 border-t border-gray-100 flex justify-end">
                <span className="text-xs font-medium">
                  Razem:{' '}
                  <span className="text-primary">
                    {(product.price * product.quantity).toFixed(2)} zł
                  </span>
                </span>
              </div>
            </motion.div>
          ))
        ) : (
          <motion.div
            className="p-6 text-center text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>Brak produktów w liście zakupowej</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ProductList;
